// src/admin/Customers.jsx
import { useState, useEffect } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../services/firebase";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

/* ── Search bar ── */
.cust-search {
  background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08);
  border-radius:6px; padding:10px 14px; color:#fff;
  font-family:'Inter',sans-serif; font-size:13px; outline:none;
  width:100%; max-width:300px; transition:border-color 0.15s;
}
.cust-search:focus { border-color:rgba(0,120,242,0.4); }

/* ── Desktop table ── */
.cust-table {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:10px; overflow:hidden;
}
.cust-head {
  display:grid; grid-template-columns:2fr 1fr 1fr 1fr;
  gap:12px; padding:11px 18px;
  border-bottom:1px solid rgba(255,255,255,0.06);
}
.cust-head-cell { font-size:10px; font-weight:700; color:#6b6b6b; text-transform:uppercase; letter-spacing:1px; }
.cust-row {
  display:grid; grid-template-columns:2fr 1fr 1fr 1fr;
  gap:12px; padding:13px 18px;
  border-bottom:1px solid rgba(255,255,255,0.04);
  font-size:13px; color:#a3a3a3; align-items:center;
  transition:background 0.12s;
}
.cust-row:last-child { border-bottom:none; }
.cust-row:hover { background:rgba(255,255,255,0.02); }

/* ── Mobile cards ── */
.cust-card {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:10px; padding:14px 16px; margin-bottom:8px;
  display:none;
}
.cust-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; margin-bottom:8px; }
.cust-card-stats { display:flex; gap:16px; }
.cust-stat { text-align:center; }

.role-pill { padding:3px 10px; border-radius:4px; font-size:10px; font-weight:600; letter-spacing:0.5px; }

.skel { background:#222; border-radius:6px; animation:skPulse 1.5s ease infinite; }
@keyframes skPulse { 0%,100%{opacity:.5} 50%{opacity:1} }

/* ════════════════════
   MOBILE ≤ 768px
════════════════════ */
@media(max-width:768px) {
  .cust-search { max-width:100%; }
  .cust-table { display:none; }
  .cust-card  { display:block; }
}
`;

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");

  useEffect(() => {
    getDocs(query(collection(db, "users"), orderBy("createdAt", "desc")))
      .then(snap => { setCustomers(snap.docs.map(d => ({ id:d.id, ...d.data() }))); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = search.trim()
    ? customers.filter(c =>
        (c.email||"").toLowerCase().includes(search.toLowerCase()) ||
        (c.displayName||"").toLowerCase().includes(search.toLowerCase())
      )
    : customers;

  function fmtDate(ts) {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { day:"numeric", month:"short", year:"numeric" });
  }

  function RolePill({ role }) {
    const isAdmin = role === "admin";
    return (
      <span className="role-pill" style={{
        background:  isAdmin ? "rgba(0,120,242,0.12)"      : "rgba(255,255,255,0.04)",
        border:      `1px solid ${isAdmin ? "rgba(0,120,242,0.3)" : "rgba(255,255,255,0.08)"}`,
        color:       isAdmin ? "#0078F2" : "#6b6b6b",
      }}>{role || "customer"}</span>
    );
  }

  return (
    <>
      <style>{S}</style>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:10 }}>
        <div style={{ fontSize:16, fontWeight:800, color:"#fff" }}>👥 Customers</div>
        <input className="cust-search" placeholder="Search by email or name…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div style={{ fontSize:12, color:"#6b6b6b", marginBottom:12, fontWeight:500 }}>
        {filtered.length} customers
      </div>

      {/* ── Desktop table ── */}
      <div className="cust-table">
        <div className="cust-head">
          <div className="cust-head-cell">User</div>
          <div className="cust-head-cell">Role</div>
          <div className="cust-head-cell">Orders</div>
          <div className="cust-head-cell">Joined</div>
        </div>
        {loading ? (
          [...Array(5)].map((_,i) => (
            <div key={i} style={{ padding:"14px 18px" }}>
              <div className="skel" style={{ height:12 }} />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div style={{ padding:"40px", textAlign:"center", color:"#6b6b6b", fontSize:13 }}>No customers found</div>
        ) : filtered.map(c => (
          <div key={c.id} className="cust-row">
            <div>
              <div style={{ color:"#fff", fontWeight:600 }}>{c.displayName || "—"}</div>
              <div style={{ fontSize:11, color:"#6b6b6b", marginTop:2 }}>{c.email}</div>
            </div>
            <div><RolePill role={c.role} /></div>
            <div style={{ fontWeight:700, color:"#fff" }}>{c.totalOrders || 0}</div>
            <div style={{ color:"#6b6b6b" }}>{fmtDate(c.createdAt)}</div>
          </div>
        ))}
      </div>

      {/* ── Mobile cards ── */}
      {loading ? (
        [...Array(4)].map((_,i) => (
          <div key={i} className="cust-card">
            <div className="skel" style={{ height:48 }} />
          </div>
        ))
      ) : filtered.length === 0 ? (
        <div className="cust-card" style={{ textAlign:"center", color:"#6b6b6b", fontSize:13 }}>No customers found</div>
      ) : filtered.map(c => (
        <div key={c.id} className="cust-card">
          <div className="cust-card-top">
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:3 }}>{c.displayName || "—"}</div>
              <div style={{ fontSize:12, color:"#6b6b6b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.email}</div>
            </div>
            <RolePill role={c.role} />
          </div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div className="cust-card-stats">
              <div className="cust-stat">
                <div style={{ fontSize:18, fontWeight:800, color:"#fff" }}>{c.totalOrders || 0}</div>
                <div style={{ fontSize:10, color:"#6b6b6b" }}>orders</div>
              </div>
            </div>
            <div style={{ fontSize:11, color:"#6b6b6b" }}>Joined {fmtDate(c.createdAt)}</div>
          </div>
        </div>
      ))}
    </>
  );
}